import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import { useNavigate } from 'react-router-dom';
import ProductAutocomplete from '../../components/common/ProductAutocomplete';

const MobileStockTransfer = () => {
    const { user } = useAuth();
    const { showToast } = useUI();
    const navigate = useNavigate();
    const [warehouses, setWarehouses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        sku: '',
        fromWarehouseId: '',
        toWarehouseId: '',
        quantity: 1,
        unit: 'Unité',
        description: ''
    });

    const fetchData = useCallback(async () => {
        if (!user?.companyId) return;
        try {
            const wRes = await fetch(`/api/warehouses?companyId=${user.companyId}`);
            if (wRes.ok) {
                const wData = await wRes.json();
                setWarehouses(wData);
                if (wData.length >= 2 && !formData.fromWarehouseId) {
                    setFormData(prev => ({ ...prev, fromWarehouseId: wData[0].id }));
                }
            }
        } catch (error) {
            console.error(error);
        }
    }, [user?.companyId, formData.fromWarehouseId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleTransfer = async (e) => {
        e.preventDefault();
        if (!formData.sku || !formData.fromWarehouseId || !formData.toWarehouseId || formData.quantity <= 0) {
            showToast("Veuillez remplir tous les champs", "warning");
            return;
        }
        if (formData.fromWarehouseId === formData.toWarehouseId) {
            showToast("Entrepôts identiques !", "warning");
            return;
        }

        setLoading(true);
        try {
            const prodRes = await fetch(`/api/products/search?sku=${formData.sku}&warehouseId=${formData.fromWarehouseId}&companyId=${user.companyId}`);
            const prodData = await prodRes.json();
            if (!prodRes.ok || !prodData || prodData.length === 0) {
                showToast("Produit non trouvé au départ", "error");
                setLoading(false);
                return;
            }
            const product = prodData[0];
            if (product.quantity < formData.quantity) {
                showToast(`Stock insuffisant (${product.quantity} dispo)`, "error");
                setLoading(false);
                return;
            }

            const response = await fetch('/api/stock-movements/transfer', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    productId: product.id,
                    fromWarehouseId: formData.fromWarehouseId,
                    toWarehouseId: formData.toWarehouseId,
                    quantity: formData.quantity,
                    unit: formData.unit,
                    userId: user.id,
                    companyId: user.companyId,
                    description: formData.description
                })
            });

            if (response.ok) {
                showToast("Transfert validé", "success");
                setFormData(prev => ({ ...prev, sku: '', quantity: 1, description: '' }));
            } else {
                const error = await response.json();
                showToast(error.error || "Erreur lors du transfert", "error");
            }
        } catch (error) {
            console.error(error);
            showToast("Erreur serveur", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 shadow-sm">
                <form onSubmit={handleTransfer} className="space-y-6">
                    {/* Warehouses Selection */}
                    <div className="flex flex-col gap-4">
                        <div className="flex-1 space-y-1">
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Entrepôt de Départ</label>
                            <div className="relative">
                                <select
                                    className="w-full h-12 bg-slate-50 dark:bg-slate-800 border-2 border-primary/20 rounded-xl px-4 font-bold text-slate-900 dark:text-white appearance-none outline-none focus:border-primary transition-all"
                                    value={formData.fromWarehouseId}
                                    onChange={e => setFormData({ ...formData, fromWarehouseId: e.target.value })}
                                >
                                    <option value="">-- Choisir --</option>
                                    {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                                </select>
                                <span className="material-symbols-outlined absolute right-3 top-3 text-primary pointer-events-none">warehouse</span>
                            </div>
                        </div>

                        <div className="flex justify-center -my-2 relative z-10">
                            <div className="size-10 rounded-full bg-primary text-white flex items-center justify-center shadow-lg border-4 border-white dark:border-slate-900">
                                <span className="material-symbols-outlined font-black">arrow_downward</span>
                            </div>
                        </div>

                        <div className="flex-1 space-y-1">
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Entrepôt de Destination</label>
                            <div className="relative">
                                <select
                                    className="w-full h-12 bg-slate-50 dark:bg-slate-800 border-2 border-emerald-500/20 rounded-xl px-4 font-bold text-slate-900 dark:text-white appearance-none outline-none focus:border-emerald-500 transition-all"
                                    value={formData.toWarehouseId}
                                    onChange={e => setFormData({ ...formData, toWarehouseId: e.target.value })}
                                >
                                    <option value="">-- Choisir --</option>
                                    {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                                </select>
                                <span className="material-symbols-outlined absolute right-3 top-3 text-emerald-500 pointer-events-none">factory</span>
                            </div>
                        </div>
                    </div>

                    <div className="h-px bg-slate-100 dark:bg-slate-800 mx-2" />

                    {/* Product Selection */}
                    <div className="space-y-4">
                        <div className="space-y-1">
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Article à transférer</label>
                            <ProductAutocomplete
                                value={formData.sku}
                                onChange={val => setFormData({ ...formData, sku: val })}
                                onSelect={product => setFormData({ ...formData, sku: product.sku })}
                                companyId={user?.companyId}
                                warehouseId={formData.fromWarehouseId}
                                placeholder="Rechercher par nom ou SKU..."
                            />
                        </div>

                        <div className="flex gap-3">
                            <div className="w-1/3 space-y-1">
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Unité</label>
                                <select
                                    className="w-full h-12 bg-slate-50 dark:bg-slate-800 border-none rounded-xl px-3 font-bold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-primary"
                                    value={formData.unit}
                                    onChange={e => setFormData({ ...formData, unit: e.target.value })}
                                >
                                    <option value="Unité">Unité</option>
                                    <option value="Carton">Carton</option>
                                    <option value="Palette">Palette</option>
                                </select>
                            </div>
                            <div className="flex-1 space-y-1">
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Quantité</label>
                                <input
                                    type="number"
                                    min="1"
                                    className="w-full h-12 bg-slate-50 dark:bg-slate-800 border-none rounded-xl px-4 font-black text-xl text-center text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-primary"
                                    value={formData.quantity}
                                    onChange={e => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                                />
                            </div>
                        </div>

                        <div className="space-y-1">
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Note (optionnel)</label>
                            <textarea
                                className="w-full h-20 bg-slate-50 dark:bg-slate-800 border-none rounded-xl p-3 font-medium text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-primary resize-none"
                                placeholder="Motif du transfert..."
                                value={formData.description}
                                onChange={e => setFormData({ ...formData, description: e.target.value })}
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-4 bg-primary text-white font-black rounded-xl text-lg shadow-lg shadow-primary/20 active:scale-95 transition-all disabled:opacity-50"
                    >
                        {loading ? "Chargement..." : "Valider le Transfert"}
                    </button>
                </form>
            </div>

            <div className="px-4 text-center">
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest leading-relaxed">
                    <span className="material-symbols-outlined text-sm align-middle mr-1">history</span>
                    Les transferts impactent les stocks en temps réel
                </p>
            </div>
        </div>
    );
};

export default MobileStockTransfer;
